import { CASCADE_TRACING_API_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AttachOrderDocumentsPayload,
  AttachOrderDocumentsResult,
  BulkImportOrdersPayload,
  BulkImportOrdersResult,
  BulkUpdateOrderHubPayload,
  BulkUpdateOrderHubResult,
  CheckOrderNumbersPayload,
  CheckOrderNumbersResult,
  CmmIntegration,
  CreateOrderHubOrderPayload,
  ExportOrdersPayload,
  ImportCompany,
  LinkTracingRequestPayload,
  OrderHubAttachment,
  OrderHubListResponse,
  OrderHubOrder,
  OrderHubOrderLine,
  OrderHubSupplierCountry,
  OrdersReadinessPayload,
  OrdersReadinessResult,
  ReferenceTracingRequestPayload,
  ResolveCompaniesPayload,
  ResolveCompaniesResult,
  SendToCmmPayload,
  SendToCmmResult,
  SendToTracingPayload,
  SendToTracingResult,
  TracingLinkPreview,
  UpdateOrderHubOrderPayload,
} from './types';

export async function fetchOrderHubOrders(
  qs: string,
): Promise<OrderHubListResponse<OrderHubOrder>> {
  return apiFetch<OrderHubListResponse<OrderHubOrder>>(
    `/orders?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubSupplierCountries(): Promise<{
  data: OrderHubSupplierCountry[];
}> {
  return apiFetch<{ data: OrderHubSupplierCountry[] }>(
    '/orders/filter-options/supplier-countries',
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubOrder(
  id: string,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    `/orders/${id}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function createOrderHubOrder(
  payload: CreateOrderHubOrderPayload,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    '/orders',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function updateOrderHubOrder(
  id: string,
  payload: UpdateOrderHubOrderPayload,
): Promise<{ data: OrderHubOrder }> {
  return apiFetch<{ data: OrderHubOrder }>(
    `/orders/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function deleteOrderHubOrder(id: string): Promise<void> {
  return apiFetch<void>(
    `/orders/${id}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

export async function bulkUpdateOrderHubOrders(
  body: BulkUpdateOrderHubPayload,
): Promise<{ data: BulkUpdateOrderHubResult }> {
  return apiFetch<{ data: BulkUpdateOrderHubResult }>(
    '/orders/bulk',
    { method: 'PATCH', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubOrderLines(
  qs: string,
): Promise<OrderHubListResponse<OrderHubOrderLine>> {
  return apiFetch<OrderHubListResponse<OrderHubOrderLine>>(
    `/order-lines?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrderHubOrderDocuments(
  qs: string,
): Promise<OrderHubListResponse<OrderHubAttachment>> {
  return apiFetch<OrderHubListResponse<OrderHubAttachment>>(
    `/order-documents?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

/**
 * Fetches EVERY line of an order by paging through the 100-capped list endpoint.
 * The declarative full-sync `PATCH /orders/:id` treats any omitted line as a
 * delete, so a line write must be built from the complete current set — never
 * the display query, which is capped at one page.
 */
export async function fetchAllOrderHubOrderLines(
  orderId: string,
): Promise<OrderHubOrderLine[]> {
  const PAGE_SIZE = 100;
  const all: OrderHubOrderLine[] = [];
  let page = 0;
  while (true) {
    const qs = new URLSearchParams({
      orderId,
      page: String(page),
      count: String(PAGE_SIZE),
    }).toString();
    const res = await fetchOrderHubOrderLines(qs);
    all.push(...res.data);
    if (res.data.length === 0 || all.length >= res.meta.totalCount) break;
    page += 1;
  }
  return all;
}

export async function sendOrderToTracing(
  id: string,
  payload: SendToTracingPayload,
): Promise<{ data: SendToTracingResult }> {
  return apiFetch<{ data: SendToTracingResult }>(
    `/orders/${id}/send-to-tracing`,
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function sendOrderToCmm(
  id: string,
  payload: SendToCmmPayload,
): Promise<{ data: SendToCmmResult }> {
  return apiFetch<{ data: SendToCmmResult }>(
    `/orders/${id}/send-to-cmm`,
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchOrdersReadiness(
  payload: OrdersReadinessPayload,
): Promise<{ data: OrdersReadinessResult }> {
  return apiFetch<{ data: OrdersReadinessResult }>(
    '/orders/readiness',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchCmmIntegration(): Promise<{ data: CmmIntegration }> {
  return apiFetch<{ data: CmmIntegration }>(
    '/orders/cmm-integration',
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function attachOrderDocuments(
  payload: AttachOrderDocumentsPayload,
): Promise<{ data: AttachOrderDocumentsResult }> {
  return apiFetch<{ data: AttachOrderDocumentsResult }>(
    '/order-documents',
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

/** Starts an async orders export. Returns the export id to poll / notify on. */
export async function exportOrderHubOrders(
  body: ExportOrdersPayload,
): Promise<{ data: { exportId: string; message: string } }> {
  return apiFetch<{ data: { exportId: string; message: string } }>(
    '/orders/export',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function bulkImportOrderHubOrders(
  body: BulkImportOrdersPayload,
): Promise<{ data: BulkImportOrdersResult }> {
  return apiFetch<{ data: BulkImportOrdersResult }>(
    '/orders/bulk-import',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function checkOrderNumbers(
  body: CheckOrderNumbersPayload,
): Promise<{ data: CheckOrderNumbersResult }> {
  return apiFetch<{ data: CheckOrderNumbersResult }>(
    '/orders/check-order-numbers',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchImportCompanies(): Promise<{
  data: ImportCompany[];
}> {
  return apiFetch<{ data: ImportCompany[] }>(
    '/orders/import-companies',
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function resolveImportCompanies(
  body: ResolveCompaniesPayload,
): Promise<{ data: ResolveCompaniesResult }> {
  return apiFetch<{ data: ResolveCompaniesResult }>(
    '/orders/resolve-companies',
    { method: 'POST', body: JSON.stringify(body) },
    CASCADE_TRACING_API_URL,
  );
}

export async function fetchTracingLinkPreview(
  orderId: string,
  qs: string,
): Promise<{ data: TracingLinkPreview }> {
  return apiFetch<{ data: TracingLinkPreview }>(
    `/orders/${orderId}/tracing-link?${qs}`,
    {},
    CASCADE_TRACING_API_URL,
  );
}

export async function linkTracingRequest(
  orderId: string,
  payload: LinkTracingRequestPayload,
): Promise<void> {
  return apiFetch<void>(
    `/orders/${orderId}/tracing-link`,
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function unlinkTracingRequest(
  orderId: string,
  tracingOrderId: string,
): Promise<void> {
  return apiFetch<void>(
    `/orders/${orderId}/tracing-link/${tracingOrderId}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}

export async function referenceTracingRequest(
  orderId: string,
  payload: ReferenceTracingRequestPayload,
): Promise<void> {
  return apiFetch<void>(
    `/orders/${orderId}/tracing-reference`,
    { method: 'POST', body: JSON.stringify(payload) },
    CASCADE_TRACING_API_URL,
  );
}

export async function removeTracingReference(
  orderId: string,
  tracingOrderId: string,
): Promise<void> {
  return apiFetch<void>(
    `/orders/${orderId}/tracing-reference/${tracingOrderId}`,
    { method: 'DELETE' },
    CASCADE_TRACING_API_URL,
  );
}
